import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface Reaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
}

interface GroupedReaction {
  emoji: string;
  count: number;
  hasUserReacted: boolean;
}

export const useMessageReactions = (chatId: string | null) => {
  const { user } = useAuthContext();
  const [reactions, setReactions] = useState<Map<string, Reaction[]>>(new Map());
  const [loading, setLoading] = useState(true);

  // Fetch all reactions for messages in this chat
  const fetchReactions = useCallback(async () => {
    if (!chatId) return;

    setLoading(true);
    
    // First get all message IDs in this chat
    const { data: messages } = await supabase
      .from("messages")
      .select("id")
      .eq("chat_id", chatId);

    if (!messages || messages.length === 0) {
      setLoading(false);
      return;
    }

    const messageIds = messages.map((m) => m.id);

    // Then fetch reactions for those messages
    const { data: reactionsData, error } = await supabase
      .from("message_reactions")
      .select("*")
      .in("message_id", messageIds);

    if (!error && reactionsData) {
      const groupedByMessage = new Map<string, Reaction[]>();
      reactionsData.forEach((reaction) => {
        const existing = groupedByMessage.get(reaction.message_id) || [];
        groupedByMessage.set(reaction.message_id, [...existing, reaction]);
      });
      setReactions(groupedByMessage);
    }
    
    setLoading(false);
  }, [chatId]);

  useEffect(() => {
    fetchReactions();
  }, [fetchReactions]);

  // Subscribe to realtime changes
  useEffect(() => {
    if (!chatId) return;

    const channel = supabase
      .channel(`reactions-${chatId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "message_reactions",
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            const newReaction = payload.new as Reaction;
            setReactions((prev) => {
              const next = new Map(prev);
              const existing = next.get(newReaction.message_id) || [];
              next.set(newReaction.message_id, [...existing, newReaction]);
              return next;
            });
          } else if (payload.eventType === "DELETE") {
            const oldReaction = payload.old as Reaction;
            setReactions((prev) => {
              const next = new Map(prev);
              const existing = next.get(oldReaction.message_id) || [];
              next.set(
                oldReaction.message_id,
                existing.filter((r) => r.id !== oldReaction.id)
              );
              return next;
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatId]);

  // Add a reaction
  const addReaction = useCallback(async (messageId: string, emoji: string) => {
    if (!user) return { error: new Error("Not authenticated") };

    const { error } = await supabase
      .from("message_reactions")
      .insert({
        message_id: messageId,
        user_id: user.id,
        emoji,
      });

    return { error };
  }, [user]);

  // Remove a reaction
  const removeReaction = useCallback(async (messageId: string, emoji: string) => {
    if (!user) return { error: new Error("Not authenticated") };

    const { error } = await supabase
      .from("message_reactions")
      .delete()
      .eq("message_id", messageId)
      .eq("user_id", user.id)
      .eq("emoji", emoji);

    return { error };
  }, [user]);

  // Toggle a reaction
  const toggleReaction = useCallback(async (messageId: string, emoji: string) => {
    if (!user) return { error: new Error("Not authenticated") };

    const messageReactions = reactions.get(messageId) || [];
    const existingReaction = messageReactions.find(
      (r) => r.user_id === user.id && r.emoji === emoji
    );

    if (existingReaction) {
      return removeReaction(messageId, emoji);
    } else {
      return addReaction(messageId, emoji);
    }
  }, [user, reactions, addReaction, removeReaction]);

  // Get grouped reactions for a message
  const getGroupedReactions = useCallback((messageId: string): GroupedReaction[] => {
    const messageReactions = reactions.get(messageId) || [];
    const emojiMap = new Map<string, { count: number; hasUserReacted: boolean }>();

    messageReactions.forEach((reaction) => {
      const existing = emojiMap.get(reaction.emoji) || { count: 0, hasUserReacted: false };
      emojiMap.set(reaction.emoji, {
        count: existing.count + 1,
        hasUserReacted: existing.hasUserReacted || reaction.user_id === user?.id,
      });
    });

    return Array.from(emojiMap.entries()).map(([emoji, data]) => ({
      emoji,
      count: data.count,
      hasUserReacted: data.hasUserReacted,
    }));
  }, [reactions, user]);

  return {
    reactions,
    loading,
    addReaction,
    removeReaction,
    toggleReaction,
    getGroupedReactions,
  };
};
